import { AccessibilityInfo, Platform } from 'react-native'
import { createURL } from 'expo-linking'

import manifest from '~/../app.json'
import pkg from '~/../package.json'

export type LogStorage = 'MEMORY' | 'SQLITE' | 'NONE'

let disableAnimations: boolean

/**
 * The `env` object allows access to the
 * environment variables from the `.env` files,
 * browser location and cookies. It also
 * allows us to coerce the type of environment
 * variables which are strings by default.
 *
 * NOTE: When adding new environment variables,
 * you'll need to add them to all of the following
 * files:
 * - `src/env.ts` (this file)
 * - `src/@types/env.d.ts`
 * - `.env` (and other associated environment files)
 */
/* istanbul ignore next: not much here */
export const env = {
  async init() {
    disableAnimations = await AccessibilityInfo.isReduceMotionEnabled()
  },

  /**
   * Animations should be disable during testing or if the user prefers reduced
   * motion.
   */
  get disableAnimations() {
    return process.env.EXPO_PUBLIC_APP_ENV === 'test' || disableAnimations
  },

  /**
   * The native version number. This is the semver version listed in the
   * `app.json` file. It is used to indicate which OTA updates are compatible
   * with which native clients. When the app gets built, this version is
   * compiled into the app and is used by the updates code to request OTA
   * updates that use the same client version.
   *
   * To get the native buildNumber/versionCode use
   * the `nativeBuildVersion` function from `expo-application`.
   */
  get appVersion() {
    return Platform.OS === 'web' ? 'web' : manifest.expo.version
  },

  /**
   * The JS version number. This is the semver version listed in the
   * `package.json` file. It is used to tell us exactly which version of the JS is
   * running on the current client.
   */
  get jsVersion() {
    return pkg.version
  },

  /**
   * The package name (aka bundle identifier) for the app.
   */
  get packageName() {
    return manifest.expo.ios.bundleIdentifier
  },

  /**
   * Get the URL scheme used for this version of the app.
   */
  get scheme() {
    return createURL('')
  },

  get verbose() {
    return process.env.EXPO_PUBLIC_VERBOSE === 'true'
  },

  /**
   * Opt-in escape hatch to dump env vars on startup even when
   * `env.production` is true. Set in overlays like `.env.prod-dev` where the
   * app is intentionally identifying as production (to exercise prod code
   * paths) but the developer still needs visibility into what's loaded. Must
   * never be set in the real production EAS environment.
   */
  get logEnv() {
    return process.env.EXPO_PUBLIC_LOG_ENV === 'true'
  },

  /**
   * Which log storage mechanism to use
   */
  get logStorage() {
    return String(
      process.env.EXPO_PUBLIC_LOG_STORAGE || 'NONE',
    ).toUpperCase() as LogStorage
  },

  get environment() {
    return process.env.EXPO_PUBLIC_APP_ENV
  },

  get production() {
    return (
      process.env.EXPO_PUBLIC_APP_ENV === 'production' ||
      process.env.EXPO_PUBLIC_APP_ENV === 'prod'
    )
  },

  get stage() {
    return process.env.EXPO_PUBLIC_APP_ENV === 'stage'
  },

  get test() {
    return process.env.EXPO_PUBLIC_APP_ENV === 'test'
  },

  get development() {
    return (
      process.env.EXPO_PUBLIC_APP_ENV === 'development' ||
      process.env.EXPO_PUBLIC_APP_ENV === 'dev' ||
      process.env.EXPO_PUBLIC_APP_ENV === 'mock'
    )
  },

  get mocks() {
    return process.env.EXPO_PUBLIC_MOCKS === 'true'
  },

  get analyticsEnabled() {
    return process.env.EXPO_PUBLIC_ANALYTICS_ENABLED === 'true'
  },

  supportEmail: process.env.EXPO_PUBLIC_SUPPORT_EMAIL,
  supportURL: process.env.EXPO_PUBLIC_SUPPORT_URL,
  salesEmail: process.env.EXPO_PUBLIC_SALES_EMAIL,
  /**
   * The base URL of the deployed web app. Used as the OAuth callback URI on
   * native so Auth0 can verify the redirect via Universal Links / App Links.
   * Not set in local development environments where Universal Links are unavailable.
   */
  appUrl: process.env.EXPO_PUBLIC_APP_URL,
  /**
   * The native URI scheme for this build (e.g. `apartmentsnapshot.stage`).
   * Explicitly configured via env var so the Auth0 redirect URI is correct in
   * OTA updates — in OTA context, Constants.expoConfig.scheme comes from the
   * update manifest's expoClient field rather than the native binary, and can
   * be stale if APP_ENV wasn't set correctly when the update was published.
   */
  appScheme: process.env.EXPO_PUBLIC_APP_SCHEME,
  authDomain: process.env.EXPO_PUBLIC_AUTH_DOMAIN,
  authClientId: process.env.EXPO_PUBLIC_AUTH_CLIENT_ID,
  reportGraphAPI: process.env.EXPO_PUBLIC_REPORT_GRAPH_API,
  /**
   * The admin secret used in development for authentication to Hasura. This
   * should never be set in production.
   */
  reportGraphAdminSecret: process.env.EXPO_PUBLIC_REPORT_GRAPH_ADMIN_SECRET,
  reportRestAPI: process.env.EXPO_PUBLIC_REPORT_REST_API,
  segmentWriteKey:
    Platform.OS === 'web'
      ? process.env.EXPO_PUBLIC_SEGMENT_WRITE_KEY_WEB
      : process.env.EXPO_PUBLIC_SEGMENT_WRITE_KEY_NATIVE,

  log() {
    const color = 'color: #d3dd2d; font-weight: bold;'
    if (!env.test)
      console.log('%c[ENV]', color, 'environment:', env.environment)
    if (env.logEnv || (!env.production && env.verbose)) {
      for (const key in env) {
        const blacklist = [
          'log',
          'init',
          'environment',
          'reportGraphAdminSecret',
        ]
        if (blacklist.indexOf(key) === -1) {
          console.log('%c[ENV]', color, `${key}:`, env[key])
        }
      }
    }
  },
}
